/**
 * ADR-0068: Per-row kebab menu on scoped list pages (Pages/Menus/PDOs/Schedules).
 * Minimal v1 action: Delete. More actions can be added here as needed.
 *
 * Parent renders this in the row's Actions cell and supplies `onDeleted` to
 * re-fetch the list after a successful delete.
 */

import { useRef, useState } from "react";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { useToast } from "./Toast.js";
import { useConfirm } from "./ConfirmDialog.js";
import { ColumnFilterPopover } from "./ColumnFilterPopover.js";
import type { ScopedEntityKind } from "../lib/scope.js";

const URL_KIND: Record<ScopedEntityKind, string> = {
  page: "pages",
  menu: "menus",
  pdo: "pdos",
  workflow: "workflows",
  saved_query: "queries",
  study: "studies",
  optimize_model: "optimize/models",
};

const ENTITY_LABEL: Record<ScopedEntityKind, string> = {
  page: "page",
  menu: "menu",
  pdo: "PDO",
  workflow: "schedule",
  saved_query: "saved query",
  study: "study",
  optimize_model: "optimization model",
};

export function ScopedRowActionsMenu(props: {
  entityKind: ScopedEntityKind;
  entityId: string;
  entityName: string;
  onDeleted: () => void | Promise<void>;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);

  async function handleDelete() {
    setOpen(false);
    const ok = await confirm.confirm({
      title: `Delete this ${ENTITY_LABEL[props.entityKind]}?`,
      detail: `"${props.entityName}" will be permanently removed. Dependents may block the delete.`,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await apiFetch(
        `/api/tenant/${URL_KIND[props.entityKind]}/${encodeURIComponent(props.entityId)}`,
        { method: "DELETE" }
      );
      toast.push({ kind: "success", message: "Deleted." });
      await props.onDeleted();
    } catch (e) {
      toast.push({
        kind: "error",
        message: e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="btn"
        aria-label={`Actions for ${props.entityName}`}
        title="Actions"
        style={{ padding: "0 6px", fontSize: 14, lineHeight: 1 }}
        disabled={busy}
        onClick={() => setOpen((v) => !v)}
      >
        ⋮
      </button>
      {open && (
        <ColumnFilterPopover
          anchor={btnRef.current}
          onClose={() => setOpen(false)}
          hideActions
          minWidth={160}
        >
          <div style={{ display: "grid", gap: 2 }}>
            <button
              type="button"
              className="btn btnDanger"
              style={{ justifyContent: "flex-start", textAlign: "left" }}
              onClick={() => void handleDelete()}
            >
              Delete
            </button>
          </div>
        </ColumnFilterPopover>
      )}
    </>
  );
}
